/**
 * TicketsSection — escalera de Tickets de descuento (Ticket Club) del prospecto.
 * Vive dentro de un <AccordionSection> en ProspectPanel (molde: ProspectCampaignsSection
 * y SupervisorAnalysis). Sesión por cookie httpOnly same-origin (patrón F2): fetch sin
 * header Authorization.
 *
 * Flujo del agente: ver la escalera → "Generar ticket nivel N" con duración (presets de
 * config + días custom hasta el tope) → copiar el código y mandarlo por WhatsApp →
 * anular si se arrepiente. El canje/vencimiento los marca el backend (lazy + E4).
 *
 * La duración se expresa en DÍAS y el ticket muere a las 23:59:59 hora Chile del día objetivo
 * (pedido de Carlos): "1 día" es hasta el final de mañana, no 24 h contadas desde ahora.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'

const ESTADO_LABEL = {
  active: 'Vigente',
  redeemed: 'Canjeado',
  expired: 'Vencido',
  cancelled: 'Anulado',
}
const ESTADO_CLASE = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  redeemed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  cancelled: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
}

// El vencimiento es a las 23:59 del día objetivo, así que "1 día" = "hasta mañana": decirlo así
// evita que el agente le prometa al cliente 24 horas exactas desde la emisión.
function diasLabel(d) {
  if (d === 1) return '1 día (hasta mañana)'
  return `${d} días`
}

export default function TicketsSection({ contactPhone }) {
  const { user } = useAuth() // is_admin gatea el reset de escalera (el backend re-valida igual)
  const [escalera, setEscalera] = useState(null) // { tickets, activo, proximoNivel, puedeGenerar, motivo }
  const [config, setConfig] = useState(null)     // { presetDays, maxCustomDays }
  const [dias, setDias] = useState(null)         // duración elegida (preset o días custom)
  const [customDias, setCustomDias] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')
  const [copiado, setCopiado] = useState(null)   // id del ticket recién copiado (feedback)

  const cargar = useCallback(() => {
    if (!contactPhone) return
    fetch(`/api/crm-discount-tickets/ladder/${encodeURIComponent(contactPhone)}`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setEscalera(d) })
      .catch(() => {})
  }, [contactPhone])

  useEffect(() => {
    cargar()
    fetch('/api/crm-discount-tickets/config')
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setConfig(d.config)
          // Preseleccionar el primer preset (1 día por defecto).
          if (d.config.presetDays && d.config.presetDays.length) setDias(d.config.presetDays[0])
        }
      })
      .catch(() => {})
  }, [cargar])

  async function generar() {
    const d = dias === 'custom' ? parseInt(customDias, 10) : dias
    if (!d || d < 1) { setError('Elige una duración'); return }
    setOcupado(true)
    setError('')
    try {
      const r = await fetch('/api/crm-discount-tickets/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: contactPhone, days: d }),
      })
      // OJO: no llamar `d` a la respuesta — shadowing con TDZ sobre la `d` de la duración
      // (el body del fetch, mismo bloque, la resolvía antes de inicializarse → ReferenceError
      // → catch → "Sin conexión" sin que el POST saliera del navegador). INC 2026-07-30.
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'No se pudo emitir el ticket'); return }
      cargar()
    } catch {
      setError('Sin conexión con el servidor')
    } finally {
      setOcupado(false)
    }
  }

  async function anular(id) {
    setOcupado(true)
    setError('')
    try {
      const r = await fetch(`/api/crm-discount-tickets/${id}/cancel`, { method: 'POST' })
      const d = await r.json()
      if (!r.ok) { setError(d.error || 'No se pudo anular'); return }
      cargar()
    } catch {
      setError('Sin conexión con el servidor')
    } finally {
      setOcupado(false)
    }
  }

  async function resetearEscalera() {
    // Confirmación dura: el reset anula el ticket vigente en la TIENDA y archiva todo el
    // historial del prospecto (auditado). Solo admin (el backend valida is_admin igual).
    if (!window.confirm('¿Resetear la escalera de este prospecto? Se anula el ticket vigente en la tienda y la escalera parte de cero (el historial queda archivado).')) return
    setOcupado(true)
    setError('')
    try {
      const r = await fetch(`/api/crm-discount-tickets/ladder/${encodeURIComponent(contactPhone)}/reset`, { method: 'POST' })
      const d = await r.json()
      if (!r.ok) { setError(d.error || 'No se pudo resetear'); return }
      cargar()
    } catch {
      setError('Sin conexión con el servidor')
    } finally {
      setOcupado(false)
    }
  }

  function copiar(t) {
    try {
      navigator.clipboard.writeText(t.code)
      setCopiado(t.id)
      setTimeout(() => setCopiado(null), 1500)
    } catch { /* clipboard bloqueado: el agente puede seleccionar el texto igual */ }
  }

  if (!escalera) return <div className="text-sm text-gray-400">Cargando…</div>

  const vence = (t) => new Date(t.expires_at).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })

  return (
    <div className="space-y-3">
      {/* Escalera (nuevo → viejo) */}
      {escalera.tickets.length === 0 && (
        <div className="text-sm text-gray-400">Sin tickets aún — la escalera parte en nivel 1 (mejor precio).</div>
      )}
      {escalera.tickets.map((t) => (
        <div key={t.id} className="p-2 bg-gray-50 dark:bg-surface-dark-elev rounded-lg text-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold">{t.code}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ESTADO_CLASE[t.status] || ''}`}>
                Nivel {t.level} · {ESTADO_LABEL[t.status] || t.status}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => copiar(t)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                title="Copiar código"
              >
                {copiado === t.id ? '✓ Copiado' : 'Copiar'}
              </button>
              {t.status === 'active' && (
                <button
                  onClick={() => anular(t.id)}
                  disabled={ocupado}
                  className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                >
                  Anular
                </button>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {t.status === 'active' ? `Vence ${vence(t)}` : `Emitido ${new Date(t.created_at).toLocaleDateString('es-CL')} por ${t.created_by}`}
            {t.status === 'redeemed' && t.woo_order_id ? ` · orden #${t.woo_order_id}` : ''}
          </div>
        </div>
      ))}

      {/* Generar el siguiente */}
      {escalera.puedeGenerar ? (
        <div className="space-y-2 pt-1 border-t border-gray-200 dark:border-surface-dark-border">
          <div className="flex flex-wrap items-center gap-1.5">
            {(config?.presetDays || []).map((d) => (
              <button
                key={d}
                onClick={() => setDias(d)}
                className={`text-xs px-2 py-1 rounded-full border ${
                  dias === d
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 dark:border-surface-dark-border text-gray-600 dark:text-gray-300'
                }`}
              >
                {diasLabel(d)}
              </button>
            ))}
            <button
              onClick={() => setDias('custom')}
              className={`text-xs px-2 py-1 rounded-full border ${
                dias === 'custom'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 dark:border-surface-dark-border text-gray-600 dark:text-gray-300'
              }`}
            >
              Otra…
            </button>
            {dias === 'custom' && (
              <input
                type="number"
                min="1"
                max={config?.maxCustomDays || 14}
                value={customDias}
                onChange={(e) => setCustomDias(e.target.value)}
                placeholder={`días (máx ${config?.maxCustomDays || 14})`}
                className="text-xs w-32 px-2 py-1 rounded border border-gray-300 dark:border-surface-dark-border bg-white dark:bg-surface-dark"
              />
            )}
          </div>
          <button
            onClick={generar}
            disabled={ocupado}
            className="w-full text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 disabled:opacity-50"
          >
            {ocupado ? 'Emitiendo…' : `Generar ticket nivel ${escalera.proximoNivel}`}
          </button>
        </div>
      ) : (
        <div className="text-xs text-gray-500 pt-1 border-t border-gray-200 dark:border-surface-dark-border">
          {escalera.motivo}
        </div>
      )}

      {/* Reset de escalera — SOLO admin y solo si hay historial que resetear. Destraba
          las escaleras agotadas/canjeadas y permite volver a partir del nivel 1. */}
      {user?.is_admin && escalera.tickets.length > 0 && (
        <button
          onClick={resetearEscalera}
          disabled={ocupado}
          className="text-xs text-amber-700 dark:text-amber-400 underline hover:no-underline disabled:opacity-50"
        >
          Resetear escalera (admin)
        </button>
      )}

      {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
    </div>
  )
}
